/**
 * QuestionsCard — ask the listing agent about the property, and read the answers.
 *
 * The form is the investor's, field for field: one message of 10 to 1000
 * characters and the same prompt. The marketplace's "Ask a question" opens a
 * page of its own; here it reveals the box in place. Each conversation renders
 * through the product's QuestionThread, with a follow-up box once the agent
 * has answered and a line saying the agent was emailed while it waits.
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { Textarea } from '@/common/components/textarea'
import {
  isAwaitingAgentReply,
  QuestionThread,
  type QuestionThreadEntry,
} from '@/features/properties/components/QuestionThread'
import { askPartnerQuestion, followUpPartnerQuestion, type PartnerQuestion } from '../../api/partnerLinks'
import {
  mutationFailureOf,
  readableIssue,
  usePartnerMutation,
  validationIssuesOf,
  type PartnerLinkSession,
} from '../../hooks/usePartnerMutation'
import * as page from '../PartnerPropertyPage/PartnerPropertyPage.css'
import * as styles from '../partnerCards.css'

export interface QuestionsCardProps {
  session: PartnerLinkSession
  /** The property the session is bound to. */
  propertyId: string
  /** Newest first. */
  questions: readonly PartnerQuestion[]
  /** Whether a new question may be asked; false once the property leaves the market. */
  canAsk: boolean
  /** Start with the question box revealed. */
  initialOpen?: boolean
}

/** The signed-in investor's question rules. */
const askSchema = z.object({
  message: z
    .string()
    .min(10, 'Question must be at least 10 characters')
    .max(1000, 'Question must be 1000 characters or less'),
})

/** The follow-up rules the API applies to a message on a question. */
const followUpSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, 'Write a follow-up before sending')
    .max(2000, 'Follow-up must be 2000 characters or less'),
})

type MessageFormData = z.infer<typeof askSchema>

const EMPTY_MESSAGE: MessageFormData = { message: '' }

function toThreadEntries(question: PartnerQuestion): QuestionThreadEntry[] {
  return question.entries.map((entry) => ({
    id: entry.id,
    sender: entry.sender,
    message: entry.message,
    createdAt: entry.created_at,
  }))
}

function AskForm({
  session,
  propertyId,
  onDone,
}: {
  session: PartnerLinkSession
  propertyId: string
  onDone: () => void
}) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<MessageFormData>({ resolver: zodResolver(askSchema) as never, defaultValues: EMPTY_MESSAGE })
  const ask = usePartnerMutation(session, (message: string) => askPartnerQuestion(session.token, propertyId, message))

  const onSubmit = (data: MessageFormData) => {
    ask.mutate(data.message, {
      onSuccess: () => {
        reset(EMPTY_MESSAGE)
        onDone()
      },
      onError: (error) => {
        for (const issue of validationIssuesOf(error)) {
          setError('message', { message: readableIssue(issue) })
        }
      },
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Textarea
        label="Your question"
        name="message"
        control={control}
        placeholder="E.g., What is the condition of the roof? Are there any known issues with the foundation?"
        rows={4}
        maxLength={1000}
        error={!!errors.message}
        disabled={ask.isPending}
      />
      {errors.message?.message && (
        <span className={styles.errorMessage} role="alert">
          {errors.message.message}
        </span>
      )}
      {ask.isError && mutationFailureOf(ask.error) === 'temporary' && (
        <span className={styles.errorMessage} role="alert">
          Your question was not sent. Check your connection and try again.
        </span>
      )}
      <Button type="submit" variant="outline" width="full" disabled={ask.isPending}>
        {ask.isPending ? 'Sending...' : 'Send question'}
      </Button>
      <Button type="button" variant="secondary" width="full" onClick={onDone} disabled={ask.isPending}>
        Cancel
      </Button>
    </form>
  )
}

function FollowUpForm({ session, questionId }: { session: PartnerLinkSession; questionId: string }) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<MessageFormData>({ resolver: zodResolver(followUpSchema) as never, defaultValues: EMPTY_MESSAGE })
  const followUp = usePartnerMutation(session, (message: string) =>
    followUpPartnerQuestion(session.token, questionId, message)
  )

  const onSubmit = (data: MessageFormData) => {
    followUp.mutate(data.message, {
      onSuccess: () => reset(EMPTY_MESSAGE),
      onError: (error) => {
        for (const issue of validationIssuesOf(error)) {
          setError('message', { message: readableIssue(issue) })
        }
      },
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Textarea
        label="Your follow-up"
        name="message"
        control={control}
        rows={3}
        maxLength={2000}
        error={!!errors.message}
        disabled={followUp.isPending}
      />
      {errors.message?.message && (
        <span className={styles.errorMessage} role="alert">
          {errors.message.message}
        </span>
      )}
      {followUp.isError && mutationFailureOf(followUp.error) === 'temporary' && (
        <span className={styles.errorMessage} role="alert">
          Your follow-up was not sent. Check your connection and try again.
        </span>
      )}
      <Button type="submit" variant="outline" width="full" disabled={followUp.isPending}>
        {followUp.isPending ? 'Sending...' : 'Send follow-up'}
      </Button>
    </form>
  )
}

export function QuestionsCard({ session, propertyId, questions, canAsk, initialOpen = false }: QuestionsCardProps) {
  const [open, setOpen] = useState(initialOpen && canAsk)

  return (
    <section className={page.railCard} aria-label="Questions">
      <Text as="h2" variant="body" weight="bold">
        Questions
      </Text>

      {questions.length > 0 && (
        <ul className={styles.threads} aria-label="Your questions">
          {questions.map((question) => {
            const entries = toThreadEntries(question)
            return (
              <li key={question.id} className={styles.thread}>
                <QuestionThread
                  viewer="investor"
                  otherPartyLabel="Listing agent"
                  entries={entries}
                  replySlot={
                    isAwaitingAgentReply(entries) ? (
                      <Text variant="bodyXs" color="muted">
                        The agent has been emailed. Their reply appears here.
                      </Text>
                    ) : (
                      <FollowUpForm session={session} questionId={question.id} />
                    )
                  }
                />
              </li>
            )
          })}
        </ul>
      )}

      {canAsk &&
        (open ? (
          <AskForm session={session} propertyId={propertyId} onDone={() => setOpen(false)} />
        ) : (
          <>
            <Text variant="bodySm" color="muted">
              Ask the listing agent about the property. Their answer appears here.
            </Text>
            <Button type="button" variant="outline" width="full" onClick={() => setOpen(true)}>
              Ask a question
            </Button>
          </>
        ))}
    </section>
  )
}
